// src/modules/app-views/dataSearch/pages/Historic/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect } from 'react';
import { Table, Tag, Button, Tooltip } from 'antd';
import { LinkOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useListContext } from 'components/core/ListProvider';
import useZustand from 'hooks/useZustand';
import appService from 'services/appService';
import translateX from 'utils/translateX';
import { formatCnpj, formatCpf } from '../Search/helpers';
import BackToHomeLink from 'components/core/BackToHomeLink';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const ENGINE_COLORS = {
  hod: 'blue',
  chokmah: 'purple',
  metatron: 'gold',
};

const SEARCH_TYPE_COLORS = {
  cnpj: 'cyan',
  cpf: 'green',
  name: 'orange',
  email: 'magenta',
  phone: 'geekblue',
};

const SYNC_STATUS_COLORS = {
  pending: 'default',
  synced: 'success',
  divergent: 'warning',
  orphan: 'error',
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DataSearchHistoric = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const {
    data,
    loading,
    tableKey,
    pagination,
    handleTableChanges,
    rowSelection,
    buildTableColumns,
    availableTableColumns,
    buildNumberLineColumn,
    buildCreatedAtColumn,
    buildUpdatedAtColumn,
    buildDocumentInfoIconsColumn,
    buildActionsButtonColumn,
    buildColumnProps,
    query,
    tableComponents,
    hiddenColumns,
    isEditingTable,
    TableFeatures,
  } = useListContext();

  const storedListSettings = useZustand('module', 'listSettings');
  const storedLanguage = useZustand('theme', 'language');

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const navigate = useNavigate();

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  const isMobile = appService.isMobile();

  // Serialize sort and search for stable dependencies
  const sortKey = JSON.stringify(query?.sort || []);
  const searchKey = JSON.stringify(query?.search || {});

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  // No local state needed for this component

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────
  // Rebuild columns when sort or settings change
  useEffect(() => {
    buildTableColumns?.([
      buildNumberLineColumn(),

      searchValueColumn(buildColumnProps),

      displayNameColumn(buildColumnProps),

      searchTypeColumn(buildColumnProps),

      engineColumn(buildColumnProps),

      creditCostColumn(buildColumnProps),

      successColumn(buildColumnProps),

      syncStatusColumn(buildColumnProps),

      entityLinkColumn(navigate),

      buildCreatedAtColumn(),

      buildUpdatedAtColumn(),

      buildDocumentInfoIconsColumn(),

      buildActionsButtonColumn({
        view: true,
        edit: false,
      }),
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    storedListSettings?.showNumberOfLine,
    sortKey,
    searchKey,
    storedLanguage,
    hiddenColumns,
    isEditingTable,
    query?.drawer?.folder,
  ]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  // No handlers needed - table changes handled by ListProvider

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  const tableSize = isMobile ? 'small' : 'large';
  const tableDataSource = data || [];

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────
  // No secondary effects needed

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <TableFeatures />

      <Table
        key={tableKey}
        scroll={{ x: 'max-content' }}
        bordered
        rowKey="_id"
        size={tableSize}
        columns={availableTableColumns}
        dataSource={tableDataSource}
        pagination={pagination}
        className="with-checkbox"
        loading={loading}
        onChange={handleTableChanges}
        rowSelection={rowSelection}
        components={tableComponents}
      />
    </>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DataSearchHistoric;

// *******************************************************************************
// HELPERS (COLUMN BUILDERS)
// *******************************************************************************

/**
 * Search value column - displays the searched value formatted
 */
function searchValueColumn(buildColumnProps) {
  return {
    key: 'searchValue',
    dataIndex: 'searchValue',
    title: translateX('search_value'),
    ...buildColumnProps?.('searchValue', { sort: true }),
    width: 180,
    render: (value, record) => {
      if (!value) return '-';

      // Ensure value is string
      const strValue = typeof value === 'string' ? value : String(value);

      // Format based on search type
      if (record.searchType === 'cnpj') {
        return <code style={{ fontSize: 12 }}>{formatCnpj(strValue)}</code>;
      }
      if (record.searchType === 'cpf') {
        return <code style={{ fontSize: 12 }}>{formatCpf(strValue)}</code>;
      }

      return <span style={{ fontSize: 13 }}>{strValue}</span>;
    },
  };
}

/**
 * Search type column - displays tag with type (CNPJ, CPF, etc.)
 */
function searchTypeColumn(buildColumnProps) {
  return {
    key: 'searchType',
    dataIndex: 'searchType',
    title: translateX('search_type'),
    ...buildColumnProps?.('searchType', { sort: true }),
    width: 100,
    align: 'center',
    render: function renderSearchType(value) {
      if (!value || typeof value !== 'string') return '-';
      const color = SEARCH_TYPE_COLORS[value] || 'default';
      return <Tag color={color}>{value.toUpperCase()}</Tag>;
    },
  };
}

/**
 * Engine column - displays tag with engine name
 */
function engineColumn(buildColumnProps) {
  return {
    key: 'engine',
    dataIndex: 'engine',
    title: translateX('engine'),
    ...buildColumnProps?.('engine', { sort: true }),
    width: 100,
    align: 'center',
    render: function renderEngine(value) {
      if (!value || typeof value !== 'string') return '-';
      const color = ENGINE_COLORS[value] || 'default';
      return <Tag color={color}>{value.toUpperCase()}</Tag>;
    },
  };
}

/**
 * Credit cost column - displays number of credits used
 */
function creditCostColumn(buildColumnProps) {
  return {
    key: 'creditCost',
    dataIndex: 'creditCost',
    title: translateX('credits'),
    ...buildColumnProps?.('creditCost', { sort: true }),
    width: 80,
    align: 'center',
    render: function renderCreditCost(value) {
      return value !== undefined && value !== null ? value : '-';
    },
  };
}

/**
 * Success column - displays success status
 */
function successColumn(buildColumnProps) {
  return {
    key: 'success',
    dataIndex: 'success',
    title: translateX('status'),
    ...buildColumnProps?.('success', { sort: true }),
    width: 90,
    align: 'center',
    render: function renderSuccess(value) {
      if (value === true) {
        return <Tag color="success">{translateX('success')}</Tag>;
      }
      if (value === false) {
        return <Tag color="error">{translateX('failed')}</Tag>;
      }
      return '-';
    },
  };
}

/**
 * Sync status column - displays sync status tag
 */
function syncStatusColumn(buildColumnProps) {
  return {
    key: 'syncStatus',
    dataIndex: 'syncStatus',
    title: translateX('sync_status'),
    ...buildColumnProps?.('syncStatus', { sort: true }),
    width: 110,
    align: 'center',
    render: function renderSyncStatus(value) {
      if (!value || typeof value !== 'string') return '-';
      const color = SYNC_STATUS_COLORS[value] || 'default';
      return <Tag color={color}>{translateX(value)}</Tag>;
    },
  };
}

/**
 * Entity link column - button to navigate to linked entity
 */
function entityLinkColumn(navigate) {
  return {
    key: 'entityLink',
    dataIndex: 'entityId',
    title: translateX('linked_entity'),
    width: 120,
    align: 'center',
    render: function renderEntityLink(entityId) {
      if (!entityId) {
        return (
          <Tooltip title={translateX('not_linked')}>
            <Tag color="default">{translateX('not_linked')}</Tag>
          </Tooltip>
        );
      }

      return (
        <Tooltip title={translateX('view_entity')}>
          <Button
            type="link"
            size="small"
            icon={<LinkOutlined />}
            onClick={() => navigate(`/app/data-search/${entityId}`)}
          >
            {translateX('view')}
          </Button>
        </Tooltip>
      );
    },
  };
}

/**
 * Display name column - shows the name from search result
 */
function displayNameColumn(buildColumnProps) {
  return {
    key: 'displayName',
    dataIndex: 'name',
    title: translateX('name'),
    ...buildColumnProps?.('name', { sort: true }),
    width: 250,
    ellipsis: true,
    render: function renderDisplayName(value, record) {
      // Try to get name from root, cnpjData or cpfData
      const name = value || record?.cnpjData?.name || record?.cpfData?.name || '-';
      return <span style={{ fontWeight: 500 }}>{name}</span>;
    },
  };
}
